import { useAuth0 } from '@auth0/auth0-react'
import { Button, TextInput } from '@helix-ds/react'
import { Envelope } from '@helix-ds/react/icons'
import * as Sentry from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import auth0Config from '../auth0/config/auth0'

const isValidEmailFormat = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

/** Normalize URL-decoded email: + is often decoded to space, so put + back for validation. */
const normalizeEmailFromUrl = (s: string): string => s.trim().replace(/\s/g, '+')

/** Get email from URL: prefer `email` param, else any param key/value that looks like an email. */
function getEmailFromSearchParams(searchParams: URLSearchParams): string {
  const fromEmail = normalizeEmailFromUrl(searchParams.get('email') ?? '')
  if (fromEmail && isValidEmailFormat(fromEmail)) return fromEmail
  for (const [key, value] of searchParams.entries()) {
    const candidate = normalizeEmailFromUrl(value || key)
    if (candidate && isValidEmailFormat(candidate)) return candidate
  }
  return ''
}

/**
 * Login page: email input then redirect to Auth0 with login_hint.
 * Autofills from ?email=... or any query param that looks like an email.
 * Layout: two-column card (Figma node 315:33504, GEN-2386).
 */
const Login: React.FC = () => {
  const [searchParams] = useSearchParams()
  const emailParam = getEmailFromSearchParams(searchParams)
  const { loginWithRedirect, isLoading } = useAuth0()
  const [email, setEmail] = useState(() => emailParam)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const fromUrl = getEmailFromSearchParams(searchParams)
    if (fromUrl && !email) setEmail(fromUrl)
  }, [searchParams, email])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    if (!isValidEmailFormat(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)
    try {
      await Sentry.startSpan(
        {
          op: 'ui.click',
          name: 'VariantLens Login - Sign in',
        },
        async (span) => {
          span.setAttribute('auth.flow', 'variant-lens')
          span.setAttribute('email.domain', email.split('@')[1] || 'unknown')
          const ap = auth0Config.authorizationParams
          const scope = typeof ap?.scope === 'string' ? ap.scope : 'openid profile email offline_access'
          await loginWithRedirect({
            authorizationParams: {
              redirect_uri: ap?.redirect_uri,
              response_type: 'code',
              audience: ap?.audience,
              scope,
              connection: ap?.connection,
              login_hint: email.trim(),
              prompt: 'login',
            },
          })
        }
      )
    } catch (err) {
      Sentry.captureException(err, {
        tags: { operation: 'auth.login', flow: 'variant-lens' },
      })
      setError(err instanceof Error ? err.message : 'Failed to sign in. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center overflow-y-auto py-8"
      style={{ backgroundColor: 'var(--color-bg-secondary)' }}
    >
      {/* Logo */}
      <div className="mb-16 flex justify-center">
        <img src="/icons/Inflection_Logo_Dark_LG.png" alt="Inflection Medicine" className="h-14 w-auto" />
      </div>

      {/* Two-column card */}
      <div
        className="flex w-full max-w-[1024px] overflow-hidden"
        style={{
          borderRadius: 'var(--radius-2xl)',
          border: '1px solid var(--color-border-base)',
          boxShadow: '0px 25px 50px -12px rgba(29, 41, 61, 0.25)',
          backgroundColor: 'var(--color-bg-primary)',
          minHeight: '704px',
        }}
      >
        {/* Left column — hero photo */}
        <div className="relative hidden w-1/2 overflow-hidden lg:block">
          <img
            src="/images/login-hero.jpg"
            alt="Login hero — newborn baby"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Right column — form */}
        <div className="flex w-full flex-col justify-center gap-8 px-16 py-16 lg:w-1/2">
          {/* Heading */}
          <div className="flex flex-col gap-3">
            <h1
              className="text-3xl leading-9 font-semibold tracking-tight"
              style={{ color: 'var(--color-text-heading)' }}
            >
              Welcome to Variant Lens
            </h1>
            <p className="text-base leading-7" style={{ color: 'var(--color-text-body)' }}>
              Sign in to filter, review, and visualize variant data alongside genomic alignments.
            </p>
          </div>

          {/* Well box */}
          <div
            className="rounded-2xl p-4"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-base)',
            }}
          >
            <p className="text-sm leading-5" style={{ color: 'var(--color-success-700)' }}>
              Protected access for variant review and viewing.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-2.5">
            <TextInput
              id="email"
              type="email"
              label="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting || isLoading}
              placeholder="Enter your email"
              autoComplete="email"
              required
              aria-label="Email address"
              icon={<Envelope className="h-5 w-5" aria-hidden />}
            />

            {error && (
              <div
                className="rounded-xl p-3"
                style={{ backgroundColor: 'var(--color-error-50)', border: '1px solid var(--color-error-500)' }}
              >
                <p className="text-sm" style={{ color: 'var(--color-error-700)' }} role="alert">
                  {error}
                </p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isSubmitting || isLoading || !email.trim()}
              aria-label="Continue to sign in"
              aria-busy={isSubmitting || isLoading}
              loading={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? 'Logging In…' : 'Continue'}
            </Button>
          </form>

          {/* Footer disclosure */}
          <p className="text-sm leading-5 font-medium" style={{ color: 'var(--color-text-subtle)' }}>
            By continuing, you agree to Inflection Medicine&apos;s{' '}
            <a
              href="https://inflectionmedicine.com/documents/inflection-medicine-privacy-policy-jan2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: 'var(--color-success-700)' }}
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="https://inflectionmedicine.com/documents/inflection-website-terms-and-conditions.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: 'var(--color-success-700)' }}
            >
              Terms &amp; Conditions
            </a>
            . Your use of this platform is governed by these terms.
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
